/**
 * Names on user_profiles may be empty: an account can exist before its owner
 * gives a name (email code sign-up, the embed widget, old imports). The
 * database stores what the person gave, never an invented value; the
 * fallback below is for display only and is never written back.
 */

type NameParts = {
  first_name?: string | null;
  last_name?: string | null;
  patronymic?: string | null;
};

const clean = (v: string | null | undefined) => (v ?? '').trim();

/** True when the person has given both a first and a last name. */
export function hasFullName<T extends NameParts>(
  p: T | null | undefined
): p is T & { first_name: string; last_name: string } {
  return !!p && clean(p.first_name) !== '' && clean(p.last_name) !== '';
}

/**
 * The name as given, parts joined with single spaces; '' when none.
 * order 'first' -> "Ольга Головецька"; 'last' -> "Головецька Ольга Антонівна"
 * (ПІБ, with patronymic).
 */
export function personName(p: NameParts | null | undefined, order: 'first' | 'last' = 'first'): string {
  if (!p) return '';
  const parts = order === 'last'
    ? [p.last_name, p.first_name, p.patronymic]
    : [p.first_name, p.last_name];
  return parts.map(clean).filter(Boolean).join(' ');
}

/** The first name, or '' (callers write "Вітаємо!" rather than "Вітаємо, !"). */
export function firstNameOf(p: NameParts | null | undefined): string {
  return clean(p?.first_name);
}

/**
 * For screens that must show something: the name, else the email, else a
 * generic «Користувач» / "User". Never store the result.
 */
export function displayName(
  p: (NameParts & { email?: string | null }) | null | undefined,
  language: string = 'uk',
  order: 'first' | 'last' = 'first'
): string {
  return personName(p, order) || clean(p?.email) || (language === 'en' ? 'User' : 'Користувач');
}
